import React from "react";
import { Star } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import Reveal from "./Reveal";

// Hotel partner asli CEO Travel di Batam.
const HOTELS = [
  {
    name: "Hotel Nagoya Hill",
    area: "Nagoya",
    stars: 4,
    img: "/hotel-nagoya-hill.jpeg",
    captions: {
      id: "Terhubung langsung dengan Nagoya Hill Mall — lokasi paling strategis untuk shopping & kuliner di jantung Nagoya.",
      en: "Directly connected to Nagoya Hill Mall — the most strategic spot for shopping & dining in the heart of Nagoya.",
      zh: "与 Nagoya Hill Mall 直通 — 位于 Nagoya 市中心，购物餐饮首选地段。",
      ja: "ナゴヤヒルモール直結 — ナゴヤ中心部でショッピングとグルメに最適な立地。",
      ko: "Nagoya Hill Mall과 직접 연결 — 나고야 중심가에서 쇼핑과 미식에 가장 좋은 위치.",
    },
    span: "lg:col-span-2",
  },
  {
    name: "Hotel 89",
    area: "Nagoya",
    stars: 3,
    img: "/hotel-89.jpeg",
    captions: {
      id: "Hotel nyaman dengan harga bersahabat di kawasan Nagoya. Pilihan tepat untuk trip singkat maupun keluarga.",
      en: "A comfortable, budget-friendly hotel in the Nagoya area. A great pick for short trips or family stays.",
      zh: "Nagoya 区域舒适实惠的酒店，短途旅行或家庭出游的理想之选。",
      ja: "ナゴヤエリアの快適でリーズナブルなホテル。短期旅行やファミリー滞在に最適。",
      ko: "나고야 지역의 편안하고 합리적인 호텔. 짧은 여행이나 가족 여행에 좋은 선택.",
    },
  },
  {
    name: "Aston Inn Gideon",
    area: "Gideon, Batam",
    stars: 3,
    img: "/aston-inn-gideon.jpeg",
    captions: {
      id: "Bagian dari jaringan Aston — bersih, modern, dan lokasinya mudah diakses. Cocok untuk business trip maupun liburan santai.",
      en: "Part of the Aston network — clean, modern, and easy to access. Great for business trips or a relaxed getaway.",
      zh: "隶属 Aston 品牌 — 干净现代，交通便利，适合商务出行或悠闲度假。",
      ja: "Aston系列のホテル — 清潔でモダン、アクセスも良好。出張にもリラックス旅行にも最適。",
      ko: "Aston 브랜드 호텔 — 깨끗하고 모던하며 접근성이 좋음. 출장이나 여유로운 휴가에 적합.",
    },
  },
  {
    name: "Hotel Aston Pelita",
    area: "Pelita, Batam",
    stars: 4,
    img: "/hotel-aston-pelita.jpeg",
    captions: {
      id: "Hotel bintang 4 dengan fasilitas lengkap — kolam renang, kamar luas, dan pelayanan ramah khas Aston.",
      en: "A 4-star hotel with full facilities — swimming pool, spacious rooms, and Aston's signature friendly service.",
      zh: "设施齐全的四星级酒店 — 泳池、宽敞客房，以及 Aston 一贯的贴心服务。",
      ja: "設備充実の4つ星ホテル — プール、広々とした客室、Aston ならではの温かいサービス。",
      ko: "시설이 완비된 4성급 호텔 — 수영장, 넓은 객실, Aston 특유의 친절한 서비스.",
    },
  },
  {
    name: "Aston Nagoya City Hotel",
    area: "Nagoya",
    stars: 4,
    img: "/aston-nagoya-city.jpeg",
    captions: {
      id: "Hotel city tower modern di pusat Nagoya — dekat mall besar & ferry terminal, ideal untuk wisata maupun bisnis.",
      en: "A modern city tower hotel in central Nagoya — close to major malls & the ferry terminal, ideal for leisure or business.",
      zh: "Nagoya 市中心的现代城市酒店 — 邻近大型商场与渡轮码头，休闲商务皆宜。",
      ja: "ナゴヤ中心部のモダンなシティホテル — 大型モールやフェリーターミナルに近く、観光・ビジネスに最適。",
      ko: "나고야 중심부의 모던한 시티 호텔 — 대형 몰과 페리 터미널 인근으로 관광·비즈니스에 이상적.",
    },
    span: "lg:col-span-2",
  },
];

export const Hotels = () => {
  const { t, lang } = useLang();
  return (
    <section
      id="hotel"
      data-testid="hotel-section"
      className="bg-[#FBFAF6] py-24 md:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-16">
        <div className="grid items-end gap-8 md:grid-cols-12 md:gap-12">
          <Reveal className="md:col-span-7">
            <span className="flex items-center gap-3 text-[11px] uppercase tracking-[0.28em] text-[#B7892F]">
              <span className="h-px w-8 bg-[#B7892F]" />
              {t.hotel.eyebrow}
            </span>
            <h2 className="mt-5 font-[Fraunces] text-4xl font-medium leading-[1.05] tracking-tight text-[#0C2230] sm:text-5xl">
              {t.hotel.title}
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-5">
            <p className="text-base leading-relaxed text-[#0C2230]/70 md:text-lg">
              {t.hotel.sub}
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {HOTELS.map((h, idx) => (
            <Reveal
              key={h.name}
              delay={idx * 0.06}
              className={`col-span-1 ${h.span || ""}`}
            >
              <article
                data-testid={`hotel-card-${idx}`}
                className="group relative h-[320px] overflow-hidden rounded-3xl md:h-[380px]"
              >
                <img
                  src={h.img}
                  alt={h.name}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0C2230]/90 via-[#0C2230]/25 to-transparent" />

                <div className="absolute inset-x-0 bottom-0 flex flex-col gap-2 p-5 md:p-6">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5">
                      {Array.from({ length: h.stars }).map((_, i) => (
                        <Star
                          key={`${h.name}-star-${i}`}
                          className="h-3.5 w-3.5 fill-[#B7892F] text-[#B7892F]"
                        />
                      ))}
                    </div>
                    <span className="rounded-full bg-white/15 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 backdrop-blur">
                      {h.area}
                    </span>
                  </div>
                  <h3 className="font-[Fraunces] text-xl font-medium leading-tight text-white md:text-2xl">
                    {h.name}
                  </h3>
                  <p className="max-h-0 max-w-md translate-y-2 overflow-hidden text-sm text-white/85 opacity-0 transition-all duration-500 group-hover:max-h-32 group-hover:translate-y-0 group-hover:opacity-100">
                    {h.captions[lang]}
                  </p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hotels;
